import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import { 
  getNavMenus, 
  createNavMenu, 
  updateNavMenu, 
  deleteNavMenu,
  NavMenu,
  NavMenuType
} from "@/utils/adminApi";

const typeLabels: Record<NavMenuType, string> = {
  category: "分类页",
  page: "单页面",
  link: "内部链接",
};

const typeColors: Record<NavMenuType, string> = {
  category: "bg-orange-50 text-orange-600",
  page: "bg-purple-50 text-purple-600",
  link: "bg-green-50 text-green-600",
};

const iconOptions = [
  "ri-home-line", "ri-building-line", "ri-code-box-line", "ri-palette-line",
  "ri-megaphone-line", "ri-scales-line", "ri-briefcase-line", "ri-store-line",
  "ri-tools-line", "ri-car-line", "ri-heart-pulse-line", "ri-graduation-cap-line",
  "ri-star-line", "ri-fire-line", "ri-news-line", "ri-information-line",
  "ri-phone-line", "ri-mail-line", "ri-map-pin-line", "ri-link-m",
  "ri-question-line", "ri-user-line", "ri-shield-check-line", "ri-settings-line",
];

interface NavItemForm {
  id: string;
  name: string;
  type: NavMenuType;
  target_id: string | null;
  target_url: string;
  sort_order: number;
  status: 'active' | 'inactive';
  icon: string;
  children: { id: string; label: string; href: string; target: '_self' | '_blank'; enabled: boolean }[];
}

interface NavItemModalProps {
  item: NavItemForm | null;
  onClose: () => void;
  onSave: (item: NavItemForm) => void;
}

function NavItemModal({ item, onClose, onSave }: NavItemModalProps) {
  const isNew = !item?.id || item.id.startsWith("new-");
  const [form, setForm] = useState<NavItemForm>(
    item ?? {
      id: "new-" + Date.now(),
      name: "",
      type: "link",
      target_id: null,
      target_url: "",
      sort_order: 0,
      status: "active",
      icon: "ri-link-m",
      children: [],
    }
  );
  const [childInput, setChildInput] = useState({ label: "", href: "" });
  const [showIconPicker, setShowIconPicker] = useState(false);

  const set = (k: keyof NavItemForm, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const addChild = () => {
    if (!childInput.label.trim() || !childInput.href.trim()) return;
    set("children", [
      ...form.children,
      { id: "nc-" + Date.now(), label: childInput.label, href: childInput.href, target: "_self" as const, enabled: true },
    ]);
    setChildInput({ label: "", href: "" });
  };

  const removeChild = (id: string) => {
    set("children", form.children.filter((c) => c.id !== id));
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.target_url.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-800">{isNew ? "新增栏目" : "编辑栏目"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">栏目名称 <span className="text-red-400">*</span></label>
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="如：企业服务"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">栏目类型</label>
              <select
                value={form.type}
                onChange={(e) => set("type", e.target.value as NavMenuType)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
              >
                <option value="category">分类页（内部分类）</option>
                <option value="page">单页面</option>
                <option value="link">内部链接</option>
              </select>
            </div>
          </div>

          {/* Link */}
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">
              链接地址 <span className="text-red-400">*</span>
              {form.type === "link" && (
                <span className="ml-2 text-xs text-green-500">内部链接请填写路径（如 /about）</span>
              )}
            </label>
            <input
              value={form.target_url}
              onChange={(e) => set("target_url", e.target.value)}
              placeholder={form.type === "link" ? "/category/xxx" : "/page-slug"}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
            />
          </div>

          {/* Target & Icon */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">打开方式</label>
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value as 'active' | 'inactive')}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
              >
                <option value="active">启用</option>
                <option value="inactive">禁用</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">图标</label>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg shrink-0">
                  <i className={`${form.icon} text-orange-500`} />
                </div>
                <button
                  onClick={() => setShowIconPicker(!showIconPicker)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:border-orange-400 cursor-pointer text-left text-gray-500"
                >
                  选择图标
                </button>
              </div>
              {showIconPicker && (
                <div className="mt-2 p-3 border border-gray-200 rounded-lg grid grid-cols-8 gap-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => { set("icon", icon); setShowIconPicker(false); }}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${form.icon === icon ? "bg-orange-500 text-white" : "hover:bg-orange-50 text-gray-600"}`}
                    >
                      <i className={`${icon} text-base`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700">启用此栏目</label>
              <p className="text-xs text-gray-400 mt-0.5">关闭后该栏目将不显示在导航中</p>
            </div>
            <button
              onClick={() => set("status", form.status === "active" ? "inactive" : "active")}
              className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${form.status === "active" ? "bg-orange-500" : "bg-gray-300"}`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.status === "active" ? "translate-x-4" : "translate-x-0"}`} />
            </button>
          </div>

          {/* Sub Items - Note: 子栏目需要通过其他方式管理 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">下拉子栏目</label>
            <p className="text-xs text-gray-400 mb-3">添加后鼠标悬停时显示下拉菜单</p>

            {form.children.length > 0 && (
              <div className="space-y-2 mb-3">
                {form.children.map((child) => (
                  <div key={child.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-corner-down-right-line text-gray-400 text-xs" />
                    </div>
                    <span className="text-sm text-gray-700 flex-1">{child.label}</span>
                    <span className="text-xs text-gray-400 font-mono truncate max-w-32">{child.href}</span>
                    <button
                      onClick={() => removeChild(child.id)}
                      className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer shrink-0"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                value={childInput.label}
                onChange={(e) => setChildInput((p) => ({ ...p, label: e.target.value }))}
                placeholder="子栏目名称"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
              <input
                value={childInput.href}
                onChange={(e) => setChildInput((p) => ({ ...p, href: e.target.value }))}
                placeholder="链接地址"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              />
              <button
                onClick={addChild}
                className="px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                添加
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">取消</button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 cursor-pointer"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminNavMenusPage() {
  const [items, setItems] = useState<NavMenu[]>([]);
  const [editTarget, setEditTarget] = useState<NavItemForm | null>(null);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 加载导航数据
  const loadNavMenus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getNavMenus();
      setItems(data);
    } catch (err: any) {
      setError(err.message || '加载导航菜单失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNavMenus();
  }, [loadNavMenus]);

  const handleSave = async (form: NavItemForm) => {
    try {
      const isNew = !form.id || form.id.startsWith("new-");
      
      const apiData = {
        name: form.name,
        type: form.type,
        target_id: form.target_id,
        target_url: form.target_url,
        status: form.status,
        icon: form.icon,
        sort_order: form.sort_order,
      };
      
      if (isNew) {
        await createNavMenu(apiData);
      } else {
        await updateNavMenu(form.id, apiData);
      }
      
      setEditTarget(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      
      await loadNavMenus();
    } catch (err: any) {
      setError(err.message || '保存失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNavMenu(id);
      setDeleteConfirm(null);
      await loadNavMenus();
    } catch (err: any) {
      setError(err.message || '删除失败');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      const menu = items.find(i => i.id === id);
      if (menu) {
        await updateNavMenu(id, {
          status: menu.status === 'active' ? 'inactive' : 'active'
        });
        await loadNavMenus();
      }
    } catch (err: any) {
      setError(err.message || '更新失败');
    }
  };

  const moveUp = async (idx: number) => {
    if (idx === 0) return;
    try {
      const currentItem = items[idx];
      const prevItem = items[idx - 1];
      
      await updateNavMenu(currentItem.id, { sort_order: prevItem.sort_order });
      await updateNavMenu(prevItem.id, { sort_order: currentItem.sort_order });
      
      await loadNavMenus();
    } catch (err: any) {
      setError(err.message || '排序失败');
    }
  };

  const moveDown = async (idx: number) => {
    if (idx === items.length - 1) return;
    try {
      const currentItem = items[idx];
      const nextItem = items[idx + 1];
      
      await updateNavMenu(currentItem.id, { sort_order: nextItem.sort_order });
      await updateNavMenu(nextItem.id, { sort_order: currentItem.sort_order });
      
      await loadNavMenus();
    } catch (err: any) {
      setError(err.message || '排序失败');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">加载中...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">栏目管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理前台导航栏目，支持排序、内部链接和外部链接</p>
          </div>
          <div className="flex items-center gap-3">
            {error && (
              <span className="text-sm text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {error}
              </span>
            )}
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line" />
                已保存
              </span>
            )}
            <button
              onClick={() => setEditTarget({
                id: "new-" + Date.now(), 
                name: "", 
                type: "link", 
                target_id: null,
                target_url: "",
                sort_order: items.length,
                status: "active", 
                icon: "ri-link-m", 
                children: [],
              })}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              新增栏目
            </button>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-lightbulb-line text-orange-500" />
          </div>
          <div className="text-sm text-orange-700">
            <strong>使用说明：</strong>拖动排序按钮调整栏目顺序；外部链接可设置新窗口打开；每个栏目可添加下拉子栏目；关闭启用开关后该栏目将从导航中隐藏。
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg">
              <i className="ri-layout-top-line text-orange-500" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{items.length}</div>
              <div className="text-xs text-gray-400">总栏目数</div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg">
              <i className="ri-checkbox-circle-line text-green-500" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{items.filter((i) => i.status === 'active').length}</div>
              <div className="text-xs text-gray-400">已启用</div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-sky-50 rounded-lg">
              <i className="ri-external-link-line text-sky-500" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{items.filter((i) => i.type === 'category').length}</div>
              <div className="text-xs text-gray-400">分类栏目</div>
            </div>
          </div>
        </div>

        {/* List */}
        {items.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
              <i className="ri-layout-top-line text-gray-400 text-xl" />
            </div>
            <p className="text-gray-400 text-sm">暂无栏目，点击右上角新增</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div
                key={item.id}
                className={`bg-white rounded-xl border transition-all ${item.status === 'active' ? "border-gray-100" : "border-gray-100 opacity-55"}`}
              >
                <div className="flex items-center gap-3 px-4 py-3.5">
                  {/* Sort */}
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer"
                    >
                      <i className="ri-arrow-up-s-line text-sm" />
                    </button>
                    <button
                      onClick={() => moveDown(idx)}
                      disabled={idx === items.length - 1}
                      className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 cursor-pointer"
                    >
                      <i className="ri-arrow-down-s-line text-sm" />
                    </button>
                  </div>

                  {/* Rank */}
                  <span className="text-xs text-gray-300 font-mono w-4 text-center shrink-0">{idx + 1}</span>

                  {/* Icon */}
                  <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg shrink-0">
                    <i className={`${item.icon || 'ri-link-m'} text-orange-500`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800">{item.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[item.type] || typeColors.link}`}>
                        {typeLabels[item.type] || '链接'}
                      </span>
                      {item.status !== 'active' && (
                        <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">已隐藏</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-400 font-mono truncate max-w-48">{item.target_url || '/'}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-400">显示</span>
                      <button
                        onClick={() => handleToggle(item.id)}
                        className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${item.status === 'active' ? "bg-orange-500" : "bg-gray-200"}`}
                      >
                        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${item.status === 'active' ? "translate-x-4" : "translate-x-0"}`} />
                      </button>
                    </div>
                    <button
                      onClick={() => setEditTarget({
                        id: item.id,
                        name: item.name,
                        type: item.type,
                        target_id: item.target_id,
                        target_url: item.target_url || '',
                        sort_order: item.sort_order,
                        status: item.status,
                        icon: item.icon || 'ri-link-m',
                        children: item.children || [],
                      })}
                      className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-orange-400 hover:text-orange-500 cursor-pointer whitespace-nowrap"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(item.id)}
                      className="px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:border-red-300 hover:text-red-500 cursor-pointer whitespace-nowrap"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editTarget && (
        <NavItemModal
          item={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={handleSave}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="text-center font-semibold text-gray-800 mb-2">确认删除</h3>
            <p className="text-center text-sm text-gray-500 mb-6">删除后该栏目将从导航中移除，无法恢复</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 cursor-pointer"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
